import type { FC } from 'react'
import styled from '@emotion/styled'

import Image from '@segment/matcha/components/Image'
import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'
import ButtonText from '@segment/matcha/components/ButtonText'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig, type PageConfig } from '@segment/contexts/page'

import GenerativeAIPng2x from './assets/GenerativeAI@2x.png'
import GenerativeAIWebp from './assets/GenerativeAI.webp'
import GenerativeAIWebp2x from './assets/GenerativeAI@2x.webp'

const getCopy = (pageConfig: PageConfig) => ({
  overline: `Generative AI`,
  heading: `Create the most relevant customer experiences in minutes`,
  list: [
    {
      heading: `CustomerAI Generative Audiences`,
      description: `[Private Beta] Launch campaigns faster by describing the specific audience you want to reach in a simple text box, then let Segment automatically create that audience for you in minutes.`,
    },
    {
      heading: `CustomerAI Generative Email & Journeys`,
      description: `Coming soon: Spend less time building, writing, and designing campaigns and journeys and more time delivering the most personalized experience to every customer.`,
    },
    {
      heading: `CustomerAI Insights`,
      description: `Coming soon: Quickly take action on real-time campaign insights with generative recommendations for faster, better results.`,
      cta: (
        <p>
          <ButtonText iconEnd={ChevronRightIcon} href={getAbsoluteUrl(`/product/twilio-engage/channels/`, pageConfig)}>
            Learn more
          </ButtonText>
        </p>
      ),
    },
  ],
})

const Generative: FC = () => {
  const pageConfig = usePageConfig()
  const copy = getCopy(pageConfig)

  return (
    <SectionBase variant="2Col" reverse={true}>
      <SectionCopy>
        <CopyTextBase>
          <CopyText.Overline>{copy.overline}</CopyText.Overline>
          <CopyText.Heading variant="h2" tag="h3">
            {copy.heading}
          </CopyText.Heading>

          {copy.list.map(({ heading, description, cta }) => (
            <FeatureBoxBase key={heading} headerPosition="top" alignment="left">
              <FeatureBox.Body>
                <FeatureBox.Title>{heading}</FeatureBox.Title>
                <FeatureBox.Text>{description}</FeatureBox.Text>
                {cta}
              </FeatureBox.Body>
            </FeatureBoxBase>
          ))}
        </CopyTextBase>
      </SectionCopy>

      <Section.Contents>
        <picture>
          <source type="image/webp" srcSet={`${GenerativeAIWebp}, ${GenerativeAIWebp2x} 2x`} />
          <StyledImage src={GenerativeAIPng2x} alt="Generative AI" width="899" height="815" loading="lazy" />
        </picture>
      </Section.Contents>
    </SectionBase>
  )
}

const SectionCopy = styled(Section.Copy)`
  display: flex;
  align-items: center;

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    max-width: 440px;
    justify-self: end;
    align-self: center;
  }
`
const StyledImage = styled(Image)`
  filter: drop-shadow(${({ theme }) => theme.shadows[2]});
`

export default Generative
